import {
  completeSuiWalletProvisioning,
  provisionSuiWallet,
  startSuiWalletProvisioning,
  type SuiWalletProvisioningResponse,
  SuiWalletProvisioningStatus,
} from '@/api/wallet/queries';
import { type WalletAccessResponse, WalletSetupStatus } from '@/api/wallet/wallet-access';

import { readRememberedPasskey, rememberPasskey } from './remembered-wallet-passkey';
import { type WalletOperation, WalletUnlockRequiredError } from './wallet-operation';
import { type WalletIdentity, walletUnlockSession } from './wallet-unlock-session';

export enum PasskeySignInMode {
  Signup = 'signup',
  Login = 'login',
}

export interface PasskeySignIn {
  privyUserId: string;
  credentialId: string | null;
}

export interface WalletAccessPorts {
  readAccess: () => Promise<WalletAccessResponse>;
  clearPrivySession: () => Promise<void>;
  signInWithPasskey: (
    mode: PasskeySignInMode,
    credentialId: string | null,
  ) => Promise<PasskeySignIn | null>;
  getAccessToken: () => Promise<string | null>;
  getIdentityToken: () => Promise<string | null>;
  createWallet: () => Promise<{ id: string; address: string }>;
}

interface WalletAccessRequest {
  gammaUserId: string;
  useExistingPasskey: boolean;
  registeredOwners: Set<string>;
  operation: WalletOperation;
}

type WalletCreationReservation = Extract<
  SuiWalletProvisioningResponse,
  { status: SuiWalletProvisioningStatus.CreationRequired }
>;

const WRONG_PASSKEY = 'Use the passkey associated with this Gamma wallet.';
const IDENTITY_PROOF_UNAVAILABLE = 'Wallet setup is unavailable. Try again.';
const SETUP_INCOMPLETE = 'Wallet setup is incomplete. Resume with your existing passkey.';
const ACCESS_FAILED = 'Could not access your wallet. Try again.';
const REMEMBERED_PASSKEY_MISSING = 'Didn’t see your passkey? Choose another passkey.';

// Setup and unlock share one path: sign in to Privy with a wallet passkey, provision the wallet when the
// backend has none yet, and unlock only after the backend confirms a ready wallet for the same Privy user.
// Every step runs through the operation, so a lock or owner change mid-flow stops it.
export async function accessWallet(
  ports: WalletAccessPorts,
  request: WalletAccessRequest,
): Promise<boolean> {
  const { gammaUserId, operation } = request;
  const before = await operation.run(ports.readAccess);
  const signIn = await signInWithWalletPasskey(ports, request, before);
  await requireAccessToken(ports, operation);

  if (before.status !== WalletSetupStatus.Ready) {
    await provisionWallet(ports, operation, before);
  }

  await confirmReadyWallet(ports, operation, signIn.privyUserId);
  const identity: WalletIdentity = { gammaUserId, privyUserId: signIn.privyUserId };
  const unlocked = walletUnlockSession.unlock(identity, operation, Date.now());

  if (unlocked && signIn.credentialId) {
    rememberPasskey(signIn.privyUserId, signIn.credentialId);
  }

  return unlocked;
}

const isPasskeyPromptDismissed = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'NotAllowedError';

export const describeWalletAccessFailure = (error: unknown): string | null => {
  if (isPasskeyPromptDismissed(error)) {
    return null;
  }

  return error instanceof Error ? error.message : ACCESS_FAILED;
};

async function signInWithWalletPasskey(
  ports: WalletAccessPorts,
  request: WalletAccessRequest,
  before: WalletAccessResponse,
): Promise<PasskeySignIn> {
  const { operation } = request;
  const mode = choosePasskeySignIn(request, before);
  const credentialId = rememberedPasskeyFor(mode, before);
  // A leftover Privy session may belong to another passkey user; a fresh sign-in proves the passkey now.
  await operation.run(ports.clearPrivySession);
  const signIn = await requestPasskey(ports, operation, mode, credentialId);
  requireBoundPasskeyUser(signIn, before);
  // After a passkey exists, a retry must log in with it rather than create a second Privy user.
  request.registeredOwners.add(request.gammaUserId);
  return signIn;
}

// Browsers report a dismissed prompt and a passkey missing from this device with the same NotAllowedError,
// so a prompt limited to the remembered passkey points to the full list instead of failing silently.
async function requestPasskey(
  ports: WalletAccessPorts,
  operation: WalletOperation,
  mode: PasskeySignInMode,
  credentialId: string | null,
): Promise<PasskeySignIn | null> {
  try {
    return await operation.run(() => ports.signInWithPasskey(mode, credentialId));
  } catch (error) {
    if (credentialId && isPasskeyPromptDismissed(error)) {
      throw new Error(REMEMBERED_PASSKEY_MISSING);
    }

    throw error;
  }
}

function rememberedPasskeyFor(
  mode: PasskeySignInMode,
  before: WalletAccessResponse,
): string | null {
  if (mode !== PasskeySignInMode.Login || !before.privyUserId) {
    return null;
  }

  return readRememberedPasskey(before.privyUserId);
}

// Signup creates a new Privy user, so only an owner with no wallet progress and no passkey from this session gets it.
function choosePasskeySignIn(
  request: WalletAccessRequest,
  before: WalletAccessResponse,
): PasskeySignInMode {
  const resumes =
    request.useExistingPasskey ||
    before.status !== WalletSetupStatus.NotStarted ||
    request.registeredOwners.has(request.gammaUserId);
  return resumes ? PasskeySignInMode.Login : PasskeySignInMode.Signup;
}

function requireBoundPasskeyUser(
  signIn: PasskeySignIn | null,
  before: WalletAccessResponse,
): asserts signIn is PasskeySignIn {
  // A passkey of another Privy user must never unlock or provision this Gamma wallet.
  const matchesBinding = !before.privyUserId || before.privyUserId === signIn?.privyUserId;

  if (!signIn || !matchesBinding) {
    throw new Error(WRONG_PASSKEY);
  }
}

async function requireAccessToken(
  ports: WalletAccessPorts,
  operation: WalletOperation,
): Promise<void> {
  const token = await operation.run(ports.getAccessToken);

  if (!token) {
    throw new WalletUnlockRequiredError();
  }
}

async function provisionWallet(
  ports: WalletAccessPorts,
  operation: WalletOperation,
  before: WalletAccessResponse,
): Promise<void> {
  const identityToken = await readIdentityProof(ports, operation, before);
  const reservation = await operation.run(() => provisionSuiWallet(identityToken));

  if (reservation.status === SuiWalletProvisioningStatus.CreationRequired) {
    await createReservedWallet(ports, operation, reservation);
  }
}

async function readIdentityProof(
  ports: WalletAccessPorts,
  operation: WalletOperation,
  before: WalletAccessResponse,
): Promise<string | undefined> {
  // Only the first request associates a Privy user, so only it carries identity proof; the backend
  // uses the stored association afterwards and rejects proof for a different user.
  if (before.privyUserId) {
    return undefined;
  }

  const identityToken = await operation.run(ports.getIdentityToken);

  if (!identityToken) {
    throw new Error(IDENTITY_PROOF_UNAVAILABLE);
  }

  return identityToken;
}

async function createReservedWallet(
  ports: WalletAccessPorts,
  operation: WalletOperation,
  reservation: WalletCreationReservation,
): Promise<void> {
  // The backend authorizes browser creation once per reservation and verifies the result in Privy, so a
  // lost response is resumed on the next attempt instead of creating a second wallet.
  const { attemptId, capability } = reservation;
  await operation.run(() => startSuiWalletProvisioning(attemptId, capability));
  const wallet = await operation.run(ports.createWallet);
  await operation.run(() =>
    completeSuiWalletProvisioning(attemptId, {
      capability,
      providerWalletId: wallet.id,
      address: wallet.address,
    }),
  );
}

async function confirmReadyWallet(
  ports: WalletAccessPorts,
  operation: WalletOperation,
  privyUserId: string,
): Promise<void> {
  // Unlock only on the backend's own record of a ready wallet for this Privy user, not on the client's view.
  const confirmed = await operation.run(ports.readAccess);

  if (confirmed.status !== WalletSetupStatus.Ready || confirmed.privyUserId !== privyUserId) {
    throw new Error(SETUP_INCOMPLETE);
  }
}
